/* eslint-disable react/no-unescaped-entities */
import Head from 'next/head';
import Link from 'next/link';
import { Container } from 'react-bootstrap';
import { ArrowRight } from '../assets/icons';

function CookiePolicy() {
  return (
    <div className='policyContainer'>
      <Head>
        <title>Cookie Policy - 24hrdoc</title>
        <meta
          name='description'
          content='Learn how 24HrDoc uses cookies and similar tracking technologies on our website and patient platform.'
        />
        <link rel='canonical' href='https://www.24hrdoc.com/cookie-policy' />
        <meta
          property='og:image'
          content={
            'https://www.24hrdoc.com/_next/image?url=%2Fassets%2Ficons%2FLogoTransparent.png&w=256&q=75'
          }
        />
      </Head>

      <Container>
        <div
          style={{
            marginBottom: '1.5rem',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: '0.75rem',
          }}
        >
          <Link href='/'>
            <span
              style={{
                fontSize: '0.875rem',
                color: '#53575A99',
                fontFamily: 'Poppins',
                cursor: 'pointer',
              }}
            >
              Home
            </span>
          </Link>
          <ArrowRight width={16} height={16} stroke='#53575A' />
          <Link href='/cookie-policy'>
            <span
              style={{
                fontSize: '0.875rem',
                color: '#53575A',
                fontFamily: 'Poppins',
                cursor: 'pointer',
              }}
            >
              Cookie Policy
            </span>
          </Link>
        </div>

        <header>
          <h1 className='privacyh1'>
            24HrDOC <span className='gradientText'>Cookie</span> Policy
          </h1>
          <p className='privacyh2'>Last Modified: May 2025</p>
        </header>

        <div className='privacyBody'>
          <section id='introduction'>
            <h2>Introduction</h2>
            <p>
              24HrDoc, Inc. ("24HrDOC", "we", "our", or "us") uses cookies and
              similar tracking technologies on our website located at{' '}
              <a href='https://www.24hrdoc.com'>www.24hrdoc.com</a> and{' '}
              <a href='https://www.24hrdoc.ai'>www.24hrdoc.ai</a> (our
              "Website(s)") and through our patient-facing platform (the "Patient
              Platform"). This Cookie Policy explains what cookies are, how we
              use them, the types of cookies we use, and how you can control your
              cookie preferences.
            </p>
            <p>
              This Cookie Policy should be read alongside our{' '}
              <Link href='/privacy-policy'>
                <a>Privacy Policy</a>
              </Link>
              , which provides more information about how we collect and use
              personal information.
            </p>
          </section>

          <section id='what-are-cookies'>
            <h2>What Are Cookies?</h2>
            <p>
              Cookies are small text files that are stored on your computer,
              tablet, mobile phone, or other device when you visit a website.
              They allow the website to recognize your device and remember
              certain information about your visit, such as your preferences and
              actions on the site.
            </p>
            <p>
              Cookies are widely used by website owners to make their websites
              work efficiently, to provide reporting information, and to deliver
              personalized experiences to visitors. Cookies set by the website
              owner (in this case, 24HrDOC) are called "first-party cookies."
              Cookies set by parties other than the website owner are called
              "third-party cookies."
            </p>
          </section>

          <section id='how-we-use'>
            <h2>How We Use Cookies</h2>
            <p>We use cookies for the following purposes:</p>
            <ul>
              <li>
                <strong>To operate our Website:</strong> Some cookies are
                essential for the Website to function properly. Without these
                cookies, certain services you have requested cannot be provided.
              </li>
              <li>
                <strong>To remember your preferences:</strong> We use cookies to
                remember information about your preferences when you visit our
                Website, so we can personalize your experience on return visits.
              </li>
              <li>
                <strong>To analyze how our Website is used:</strong> We use
                analytics cookies to understand how visitors interact with our
                Website, which pages are most popular, and how users navigate
                through the site. This helps us improve the Website and our
                Services.
              </li>
              <li>
                <strong>To deliver relevant advertising:</strong> We may use
                cookies to show you advertisements that are more relevant to your
                interests, both on and off our Website.
              </li>
              <li>
                <strong>To maintain security:</strong> Cookies help us detect
                fraud and protect the security of our Website and your account.
              </li>
            </ul>
          </section>

          <section id='types'>
            <h2>Types of Cookies We Use</h2>

            <div className='tw-overflow-x-auto tw-my-6'>
              <table className='tw-w-full tw-border-collapse tw-border tw-border-gray-300 tw-text-sm tw-text-gray-800'>
                <thead className='tw-bg-gray-100'>
                  <tr>
                    <th className='tw-border tw-border-gray-300 tw-p-3 tw-text-left tw-font-semibold tw-w-1/4'>
                      Cookie Type
                    </th>
                    <th className='tw-border tw-border-gray-300 tw-p-3 tw-text-left tw-font-semibold tw-w-1/2'>
                      Description
                    </th>
                    <th className='tw-border tw-border-gray-300 tw-p-3 tw-text-left tw-font-semibold tw-w-1/4'>
                      Can Be Disabled?
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      <strong>Strictly Necessary</strong>
                    </td>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      These cookies are essential to enable you to use our
                      Website and its features, such as accessing secure areas
                      of the site, maintaining your session while logged in, and
                      remembering items in your session. Without these cookies,
                      core services you have requested cannot be provided.
                    </td>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      No — these are required for the Website to function.
                    </td>
                  </tr>
                  <tr>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      <strong>Functional / Preference</strong>
                    </td>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      These cookies allow the Website to remember choices you
                      make (such as your state, language, or region) and provide
                      enhanced, more personalized features. They may also be
                      used to provide services you have requested such as
                      watching a video or commenting on a blog.
                    </td>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      Yes — disabling these may affect site functionality.
                    </td>
                  </tr>
                  <tr>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      <strong>Analytics / Performance</strong>
                    </td>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      These cookies collect information about how visitors use
                      our Website — for instance, which pages visitors go to
                      most often and whether they receive error messages. This
                      information is used to improve how the Website works and
                      is typically aggregated and anonymized.
                    </td>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      Yes — you can opt out through browser settings or opt-out
                      tools.
                    </td>
                  </tr>
                  <tr>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      <strong>Advertising / Targeting</strong>
                    </td>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      These cookies are used to deliver advertisements that are
                      more relevant to you and your interests. They are also
                      used to limit the number of times you see an
                      advertisement and to help measure the effectiveness of
                      advertising campaigns. They are placed by advertising
                      networks with our permission.
                    </td>
                    <td className='tw-border tw-border-gray-300 tw-p-3 tw-align-top'>
                      Yes — you can opt out through ad network preference tools.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id='specific-cookies'>
            <h2>Specific Cookies and Technologies We Use</h2>

            <h3>Google Analytics</h3>
            <p>
              We use Google Analytics to collect information about how visitors
              use our Website. Google Analytics collects information such as how
              often users visit the Website, what pages they visit, and what
              other sites they used prior to visiting. We use this information
              only to improve our Website and Services. Google Analytics collects
              only the IP address assigned to you on the date you visit, rather
              than your name or other identifying information.
            </p>
            <p>
              Google&apos;s ability to use and share information collected by
              Google Analytics is governed by the{' '}
              <a
                href='https://policies.google.com/privacy'
                target='_blank'
                rel='noreferrer'
              >
                Google Privacy Policy
              </a>
              . You can prevent Google Analytics from recognizing you on return
              visits by disabling cookies in your browser, or by installing the{' '}
              <a
                href='https://tools.google.com/dlpage/gaoptout'
                target='_blank'
                rel='noreferrer'
              >
                Google Analytics Opt-out Browser Add-on
              </a>
              .
            </p>

            <h3>Meta Pixel</h3>
            <p>
              We use the Meta Pixel (formerly Facebook Pixel) to measure the
              effectiveness of our advertising on Meta platforms (including
              Facebook and Instagram) and to serve you relevant ads. The Pixel
              may link information about your interaction with our Website to
              your Meta account. Meta may use this information for its own
              purposes in accordance with its{' '}
              <a
                href='https://www.facebook.com/privacy/policy/'
                target='_blank'
                rel='noreferrer'
              >
                Data Policy
              </a>
              . You can manage your Meta ad preferences via your{' '}
              <a
                href='https://www.facebook.com/ads/preferences/'
                target='_blank'
                rel='noreferrer'
              >
                Meta Ad Settings
              </a>
              .
            </p>

            <h3>Session Cookies</h3>
            <p>
              We use session cookies to maintain your authenticated session
              while you are logged in to our Patient Platform. These cookies are
              strictly necessary and are deleted when you close your browser or
              log out.
            </p>
          </section>

          <section id='hipaa-note'>
            <div className='tw-bg-yellow-100 tw-border tw-border-yellow-300 tw-p-4 tw-mb-4 tw-text-sm tw-text-gray-800 tw-rounded'>
              <strong>Important — Patient Platform:</strong> We do not deploy
              analytics, advertising, or third-party tracking cookies on
              authenticated Patient Platform pages (including login,
              registration, patient intake forms, appointment scheduling, or any
              pages where Protected Health Information may be present). Tracking
              technologies are limited to unauthenticated, public-facing pages
              of the Website only.
            </div>
          </section>

          <section id='control'>
            <h2>How to Control Cookies</h2>
            <p>
              You have the right to decide whether to accept or decline cookies
              (other than strictly necessary cookies). You can exercise your
              cookie preferences in the following ways:
            </p>

            <h3>Browser Settings</h3>
            <p>
              Most web browsers allow you to control cookies through their
              settings. You can typically find these settings in the "Options,"
              "Preferences," or "Settings" menu of your browser. The following
              links provide information on how to manage cookies in common
              browsers:
            </p>
            <ul>
              <li>
                <a
                  href='https://support.google.com/chrome/answer/95647'
                  target='_blank'
                  rel='noreferrer'
                >
                  Google Chrome
                </a>
              </li>
              <li>
                <a
                  href='https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop'
                  target='_blank'
                  rel='noreferrer'
                >
                  Mozilla Firefox
                </a>
              </li>
              <li>
                <a
                  href='https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac'
                  target='_blank'
                  rel='noreferrer'
                >
                  Apple Safari
                </a>
              </li>
              <li>
                <a
                  href='https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09'
                  target='_blank'
                  rel='noreferrer'
                >
                  Microsoft Edge
                </a>
              </li>
            </ul>
            <p>
              Please note that if you disable or refuse cookies, some parts of
              the Website may become inaccessible or not function properly.
            </p>

            <h3>Opt-Out Tools</h3>
            <p>
              You can opt out of interest-based advertising from third-party
              advertisers who are members of the Network Advertising Initiative
              (NAI) or who follow the Digital Advertising Alliance's
              Self-Regulatory Principles by visiting:
            </p>
            <ul>
              <li>
                <a
                  href='https://thenai.org/how-to-opt-out/'
                  target='_blank'
                  rel='noreferrer'
                >
                  Network Advertising Initiative Opt-Out
                </a>
              </li>
              <li>
                <a
                  href='https://optout.aboutads.info/'
                  target='_blank'
                  rel='noreferrer'
                >
                  Digital Advertising Alliance Opt-Out
                </a>
              </li>
            </ul>

            <h3>Do Not Track</h3>
            <p>
              Some browsers include a "Do Not Track" feature that signals to
              websites that you visit that you do not want to have your online
              activity tracked. Our Website does not currently respond to "Do
              Not Track" signals, as there is no consistent industry standard
              for compliance. We do, however, honor Global Privacy Control (GPC)
              signals as an opt-out of targeted advertising and sharing of
              personal information, where applicable under state law.
            </p>
          </section>

          <section id='retention'>
            <h2>Cookie Retention</h2>
            <p>
              Cookies can remain on your device for different periods of time.
              Some cookies are "session cookies," meaning they are deleted when
              you close your browser. Others are "persistent cookies," meaning
              they remain on your device for a set period of time or until you
              delete them. The retention period for each cookie depends on its
              purpose. We configure persistent cookies to expire after a
              reasonable period consistent with the purpose for which they are
              set.
            </p>
          </section>

          <section id='updates'>
            <h2>Updates to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect
              changes in technology, regulation, or our business practices. Any
              changes will become effective when we post the revised Cookie
              Policy on our Website. We encourage you to periodically review
              this page for the latest information on our cookie practices. The
              "Last Modified" date at the top of this page indicates when this
              Cookie Policy was last updated.
            </p>
          </section>

          <section id='contact'>
            <h2>Contact Us</h2>
            <p>
              If you have any questions about our use of cookies or this Cookie
              Policy, please contact us at:{' '}
              <a href='mailto:services@24hrdoc.com'>services@24hrdoc.com</a>.
            </p>
            <p>
              For more information about how we handle your personal
              information generally, please review our{' '}
              <Link href='/privacy-policy'>
                <a>Privacy Policy</a>
              </Link>
              .
            </p>
          </section>

          <footer>
            <p class='small'>&copy; 24HrDoc, Inc.</p>
          </footer>
        </div>
      </Container>
    </div>
  );
}

export default CookiePolicy;
